import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js'
import { Dialog, FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, Tooltip, Typography } from '@mui/material'
import { Controller, useForm, useWatch } from 'react-hook-form'
import * as Yup from 'yup'
import type { Product } from '../../api/api'

type DiscountType = 'absolute' | 'percentage'

interface DiscountForm {
  productId: string
  discountType: string | undefined
}

export interface CreateCustomerDiscountDialogProps {
  open: boolean
  onClose: () => void
  onCreateDiscount: () => void
  currency: string
  customerId: string
  products: Product[]
  plugins: CreateCustomerDiscountPlugin[]
}

const defaultValues = ({ products, plugins }: CreateCustomerDiscountDialogProps): DiscountForm => {
  const productWithPrice = products.find((product) => !product.disabled)
  const defaultProductId = productWithPrice ? productWithPrice.id : products[0]?.id || ''
  const defaultDiscountType = plugins[0]?.name

  return {
    productId: defaultProductId,
    discountType: defaultDiscountType,
  }
}

const validationSchema = Yup.object({
  productId: Yup.string().required('Product is required'),
  discountType: Yup.mixed<DiscountType>().required('Discount type is required'),
  discountValue: Yup.number()
    .min(0, 'Discount value must be at least 0')
    .required('Discount value is required')
    .typeError('Discount value is required'),
  expirationDate: Yup.date().nullable(),
})

export const CreateCustomerDiscountDialog = (props: CreateCustomerDiscountDialogProps) => {
  const form = useForm<DiscountForm>({
    values: defaultValues(props),
    resolver: yupResolver(validationSchema),
  })

  const discountTypes = props.plugins.map((plugin) => plugin.name)

  const selectedDiscountType = useWatch({ control: form.control, name: 'discountType' })
  const productId = useWatch({ control: form.control, name: 'productId' })

  const discountPlugin = props.plugins.find((plugin) => selectedDiscountType && plugin.supports(selectedDiscountType))
  const DiscountComponent = discountPlugin?.getComponent()

  return (
    <Dialog open={props.open} onClose={props.onClose}>
      <Stack
        sx={{
          flex: 1,
          gap: 3,
          padding: 3,
          minWidth: 400,
        }}
      >
        <Typography variant="h2">Line item discount</Typography>
        <FormControl fullWidth>
          <InputLabel>Line item</InputLabel>
          <Controller
            control={form.control}
            name="productId"
            render={({ field }) => (
              <Select
                slotProps={{ input: { 'aria-label': 'line-item' } }}
                label="Line item"
                value={field.value}
                onChange={(event) => field.onChange(event.target.value)}
              >
                {props.products.map((product) => (
                  <MenuItem key={product.id} value={product.id} disabled={product.disabled}>
                    <Typography variant="p2Regular">{product.name}</Typography>
                  </MenuItem>
                ))}
              </Select>
            )}
          />

          <FormHelperText error={Boolean(form.formState.errors.productId)}>{form.formState.errors.productId?.message}</FormHelperText>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Discount method</InputLabel>
          <Select
            slotProps={{ input: { 'aria-label': 'discount-type' } }}
            label="Discount method"
            value={useWatch({ control: form.control, name: 'discountType' })}
            {...form.register('discountType')}
          >
            {discountTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error={Boolean(form.formState.errors.discountType)}>{form.formState.errors.discountType?.message}</FormHelperText>
        </FormControl>
        {DiscountComponent && (
          <DiscountComponent
            productId={productId}
            currency={props.currency}
            customerId={props.customerId}
            onCreatedDiscount={props.onCreateDiscount}
          />
        )}
      </Stack>
    </Dialog>
  )
}

export interface CreateCustomerDiscountPluginComponentProps {
  productId: string
  currency: string
  customerId: string
  onCreatedDiscount: () => void
}

export abstract class CreateCustomerDiscountPlugin {
  abstract name: string
  abstract supports(discountType: string): boolean
  abstract getComponent(): React.ComponentType<CreateCustomerDiscountPluginComponentProps>
}
